<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Button } from '@/components/ui/button';
import { useAuth, useHydrated } from '@app/composables';
import AppNav from './AppNav.vue';
import FlowHeader from './FlowHeader.vue';
import ProfileDropdown from '@app/presentation/components/shared/ProfileDropdown.vue';
import { LOGIN_TO } from '@app/presentation/components/site/constants';
import { HOME_ROUTE } from '@app/router/router';
import BrandLogo from '@app/presentation/components/shared/BrandLogo.vue';
import PaperStrip from '@app/presentation/components/paper/PaperStrip.vue';

// Hydration-safe auth: SSR/first-render always shows the guest affordance, then
// reactively reflects the real auth state after mount (a plain reactive patch).
const isHydrated = useHydrated();
const { isAuthenticated } = useAuth();
const authed = computed(() => isHydrated.value && isAuthenticated.value);

const route = useRoute();
const { t } = useI18n();
// No "log in" button on the login screen itself.
const showLogin = computed(() => !authed.value && route.name !== 'login');
</script>

<template>
  <!-- Full-viewport paper-theatre box that hosts the centered phone frame:
       page stock with grain, a low static hills silhouette at the bottom, and
       the phone as a window cut into a raised card. No gradients, orbs or blur. -->
  <div class="paper-grain fixed inset-0 flex flex-col overflow-hidden bg-background">
    <!-- Static procedural hills (two cut-paper silhouettes, each with a lit rim
         and a cast shade under its top edge). No animation, no filter. -->
    <svg
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 bottom-0 h-32 w-full"
      viewBox="0 0 1440 128"
      preserveAspectRatio="none"
    >
      <path
        class="fill-[var(--paper-shade)]"
        d="M0 58 C 150 30, 300 26, 470 52 S 800 82, 980 50 S 1290 22, 1440 44 V128 H0 Z"
        transform="translate(0 4)"
      />
      <path
        class="fill-card stroke-[var(--paper-edge)]"
        stroke-width="1.5"
        vector-effect="non-scaling-stroke"
        d="M0 58 C 150 30, 300 26, 470 52 S 800 82, 980 50 S 1290 22, 1440 44 V128 H0 Z"
      />
      <path
        class="fill-[var(--paper-shade)]"
        d="M0 96 C 220 72, 420 70, 640 88 S 1020 110, 1200 86 S 1380 74, 1440 80 V128 H0 Z"
        transform="translate(0 4)"
      />
      <path
        class="fill-muted stroke-[var(--paper-edge)]"
        stroke-width="1.5"
        vector-effect="non-scaling-stroke"
        d="M0 96 C 220 72, 420 70, 640 88 S 1020 110, 1200 86 S 1380 74, 1440 80 V128 H0 Z"
      />
    </svg>

    <!-- Thin top bar — aligned to the phone column (max-w-[600px]) so the brand
         and profile sit directly above the device, not stretched across the
         whole canvas. Brand mark matches SiteNav (landing) exactly. A torn
         paper strip (PaperStrip), solid stock, no blur. -->
    <header class="relative isolate z-10 shrink-0">
      <PaperStrip edge="bottom" wide />
      <div class="mx-auto flex h-12 w-full max-w-[600px] items-center justify-between px-4">
        <BrandLogo :to="HOME_ROUTE" />
        <div class="flex items-center gap-2">
          <ProfileDropdown v-if="authed" />
          <Button v-else-if="showLogin" as-child size="sm" variant="outline">
            <RouterLink :to="LOGIN_TO">{{ t('nav.login') }}</RouterLink>
          </Button>
        </div>
      </div>
    </header>

    <!-- Centered phone column on the canvas. -->
    <div class="relative z-10 flex min-h-0 flex-1 justify-center px-4 py-6">
      <!-- The phone as a cut card window: a raised stock frame (grain, lit rim,
           hand-cut corners, a long soft cast onto the page) with the screen
           cut into it. -->
      <div
        class="paper-sheet-raised desktop-frame-card mx-auto flex min-h-0 w-full max-w-[620px] flex-col p-2.5"
      >
        <!-- translateZ(0) makes this phone a containing block for `position: fixed`
             descendants, so the app's fixed chrome — the top Steps progress bar and
             the bottom MainButton/SecondaryButton — anchors to and clips against
             THIS frame (its rounded corners) instead of the browser viewport.
             Removing the transform re-introduces the full-viewport-width bleed. -->
        <div
          class="desktop-frame-window paper-grain relative [transform:translateZ(0)] mx-auto flex min-h-0 w-full max-w-[600px] flex-1 flex-col overflow-hidden bg-background"
        >
          <!-- The app screen scrolls inside the phone. Mobile-style 16px page
               padding (not the desktop --page-padding-x of 24px) so the screen
               matches the real Mini App instead of rendering flush to the bezel. -->
          <!-- Flush top bar (back / step progress), OUTSIDE the scroll area so
               content can never bleed above it. Renders nothing on routes without
               a parent / multi-step progress. -->
          <FlowHeader />
          <main class="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <slot />
          </main>
          <!-- Bottom tabs pinned to the column bottom; renders nothing on
               immersive (chrome: 'bare') routes. -->
          <AppNav compact />
          <!-- The cut edge of the window: a shade just inside the opening, over
               the screen (pointer-events off) so it reads as depth, not a bezel.
               z-[10000] on purpose: it must paint above EVERY in-frame fixed layer
               (MainButton 9998 / SecondaryButton 9999 bars, BottomPopup's z-50
               backdrop; reka dialogs portal to <body>, outside the frame), or
               their fills would cover the cut edge. It is
               only an inset shadow, so it never blocks input or hides content. A
               later in-frame layer above 10000 would paint over the edge — keep
               this the top-most layer inside the translateZ(0) window. -->
          <span
            aria-hidden="true"
            class="desktop-frame-window pointer-events-none absolute inset-0 z-[10000] shadow-[inset_0_2px_5px_var(--paper-shade),inset_0_0_0_1px_rgb(4_3_18/0.6)]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A phone-sized hand-cut card: never perfectly even corners (the --paper-radius
   idea at a larger scale); the window is cut ~10px inside it. */
.desktop-frame-card {
  border-radius: 30px 22px 32px 20px / 22px 30px 20px 28px;
  box-shadow:
    inset 0 1px 0 var(--paper-edge),
    0 10px 20px -10px var(--paper-shade),
    0 34px 70px -28px var(--paper-shade);
}
.desktop-frame-window {
  border-radius: 21px 14px 23px 12px / 14px 21px 12px 19px;
}
</style>
